import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {makeStyles} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import Button from '@material-ui/core/Button';
import {StyledInviteUserItem} from "../../../pro-guest/shared/user/InviteUserItem";
import CSVImport from "../../../_shared/CSVImport";
import {useNotification} from "../../../ui/notification/NotificationProvider";
import {
    ContactError,
    ContactToAdd,
    isEmptyImportedItem,
    isImportedItem,
    MachineContactModel
} from "../../../../models/machine.models";
import * as _ from 'lodash';

const useStyles = makeStyles(theme => ({
    formsContainer: {
        marginTop: theme.spacing(2)
    },
    addBtn: {
        marginTop: theme.spacing(4)
    },
    actions: {
        '& > *': {
            marginLeft: theme.spacing(2)
        }
    }
}));

const CSVFormat = {
    headers: ['Email', 'FirstName', 'LastName'],
    examples: [
        ['js.smith@gmail.com', 'John', 'Smith'],
        ['j.doe@gmail.com', 'James', 'Doe']
    ]
};

export interface ContactInviteEditorProps {
    readonly items: MachineContactModel[],
    readonly onAddItem: (contacts: ContactToAdd) => number | string;
    readonly onUpdateItem: (id: number | string, contact: Partial<ContactToAdd>) => ContactError | null;
    readonly onRemoveItem: (id: number) => void;
    readonly buildItemData: (data?: string[]) => ContactToAdd;
}

export const ContactInviteEditor = ({
                                        items,
                                        buildItemData,
                                        onAddItem,
                                        onUpdateItem,
                                        onRemoveItem,
                                    }: ContactInviteEditorProps) => {
    const {t} = useTranslation();
    const classes = useStyles();
    const {pushNotification} = useNotification();

    const resetHandler = async (e: any) => {
        items.filter(isImportedItem).map(f => f.id as number).forEach(onRemoveItem);
        onAddItem(buildItemData());
    };

    const handleFormChange = (name: string, value: string, form: MachineContactModel) => onUpdateItem(form.id, {[name]: value});

    const loadCSVData = (data: string[][]) => {
        const empty = _.find(items, isEmptyImportedItem);
        !!empty && onRemoveItem(empty.id as number);

        data
            .map((row: string[]) => buildItemData(row))
            .forEach(onAddItem)
    };

    const onLoadCSVError = (errorMsg: any, errorCode: any) => {
        const errorCodeMsg = errorCode ? '\nError code: ' + errorCode : '';
        if (errorMsg) {
            pushNotification({
                message: errorMsg + errorCodeMsg,
                severity: 'warning'
            });
        }
    };


    return <Fragment>
        <Grid container className={classes.formsContainer}>
            {
                items
                    .filter(f => typeof f.id === 'number')
                    .map((f, i) =>
                        <StyledInviteUserItem
                            form={f}
                            key={f.id.toString()}
                            handleFormDeleted={onRemoveItem}
                            handleFormChange={handleFormChange}
                            submitted={true}>
                        </StyledInviteUserItem>
                    )
            }
        </Grid>
        <Grid container justify="flex-start">
            <IconButton aria-label="add" className={classes.addBtn}
                        onClick={() => onAddItem(buildItemData())}>
                <AddIcon/>
            </IconButton>
        </Grid>
        <Grid container justify="flex-end" className={classes.actions}>
            <Button variant="outlined" color="primary" size="medium"
                    onClick={resetHandler}>
                {t('_Common.Labels.Reset')}
            </Button>
        </Grid>
        <CSVImport displayedLabel={t('_Common.Labels.CSV')}
                   format={CSVFormat}
                   onComplete={loadCSVData}
                   onError={onLoadCSVError}/>

    </Fragment>
};
